import { DateTime } from 'luxon';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { apptoken, mapstyle } from '../creds.js';

import { dom } from './dom.js';
import { mapInstance, draw, visibleTrackIds, trackDataCache, notesByTrackId, colorIndex, setMapInstance, setDraw, incrementColorIndex, isMouseOverPhoto, setIsMouseOverPhoto, isMouseOverNote, setIsMouseOverNote, photosVisible, notesVisible } from './state.js';
import { defaultTrackColors } from './config.js';
import { getShareIdFromUrl, handleSharedView, showPhotoModal, showNoteModal, handleTrimClick, handleDeletePointClick } from './ui.js';
import { getNotesForTrack } from './api.js';
import { getTimezoneFromLatLon } from './utils.js';

export function initializeMap() {
  mapboxgl.accessToken = apptoken;
  const isMobile = window.innerWidth <= 768;
  const map = new mapboxgl.Map({
    container: "map",
    style: mapstyle,
    projection: "globe",
    zoom: 1.5,
    center: [0, 0],
    preserveDrawingBuffer: !isMobile,
  });
  setMapInstance(map);

  map.addControl(new mapboxgl.NavigationControl());
  map.scrollZoom.enable();

  const mapDraw = new MapboxDraw({
    clickBuffer: 5,
    displayControlsDefault: false,
    controls: {
      trash: true
    }
  });
  setDraw(mapDraw);
  map.addControl(mapDraw);

  map.on("style.load", () => {
    map.setFog({});

    // Add a static anchor point for photo layers
    const layers = map.getStyle().layers;
    let firstSymbolId;
    for (const layer of layers) {
        if (layer.type === 'symbol') {
            firstSymbolId = layer.id;
            break;
        }
    }
    map.addSource('anchor-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
        id: 'photo-anchor',
        type: 'circle',
        source: 'anchor-source',
        paint: { 'circle-radius': 0 }
    }, firstSymbolId);


    const share = getShareIdFromUrl(window.location.pathname);
    if (share) {
      handleSharedView(share);
    }
  });

  const hoverPopup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  map.on("mousemove", (e) => {
    const photoLayers = map.getStyle().layers.filter(l => l.id.startsWith('photos-layer-') || l.id.startsWith('photos-circle-layer-')).map(l => l.id);
    if (photoLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: photoLayers });
        if (features.length > 0) {
            map.getCanvas().style.cursor = "pointer";
            setIsMouseOverPhoto(true);
            hoverPopup.remove();
            return;
        }
    }
    setIsMouseOverPhoto(false);

    const noteLayers = map.getStyle().layers.filter(l => l.id.startsWith('notes-layer-')).map(l => l.id);
    if (noteLayers.length > 0) {
        const features = map.queryRenderedFeatures(e.point, { layers: noteLayers });
        if (features.length > 0) {
            map.getCanvas().style.cursor = "pointer";
            setIsMouseOverNote(true);
            hoverPopup.remove();
            return;
        }
    }
    setIsMouseOverNote(false);

    const pointLayers = map.getStyle().layers.filter(l => l.id.startsWith('points-layer-')).map(l => l.id);
    if (pointLayers.length > 0) {
      const features = map.queryRenderedFeatures(e.point, { layers: pointLayers });
      if (features.length) {
        map.getCanvas().style.cursor = "pointer";
        const props = features[0].properties;
        const coords = features[0].geometry.coordinates.slice();
        const tz = (!props.time_zone || ['utc', 'gmt'].includes(props.time_zone.toLowerCase())) ? 'UTC' : props.time_zone;
        const time = DateTime.fromISO(props.time).setZone(tz).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
        const trackColor = props.color || '#000';
        const popupHtml = `<div style="color: ${trackColor};">
                    <div><strong>Time:</strong> ${time}</div>
                    <div><strong>Speed:</strong> ${props.speed} knots</div>
                    <div><strong>Lat:</strong> ${coords[1]}</div>
                    <div><strong>Lon:</strong> ${coords[0]}</div>
                </div>`;
        if (!isMouseOverPhoto && !isMouseOverNote) {
            hoverPopup.setLngLat(coords).setHTML(popupHtml).addTo(map);
        }
      } else {
        map.getCanvas().style.cursor = "";
        hoverPopup.remove();
      }
    }
  });

  map.on("mouseleave", 'points-layer', () => {
    map.getCanvas().style.cursor = "";
    hoverPopup.remove();
  });



  const photoIconClickHandler = (e) => {
    const photoLayers = map.getStyle().layers.filter(l => l.id.startsWith('photos-layer-') || l.id.startsWith('photos-circle-layer-')).map(l => l.id);
    if (!photoLayers.length) return;

    const features = map.queryRenderedFeatures(e.point, { layers: photoLayers });
    if (!features.length) return;

    e.originalEvent.stopImmediatePropagation();
    const props = features[0].properties;
    showPhotoModal(props.signed_url, props.photo_id, props.caption, props.can_delete, props.author_name);
  };

  const noteIconClickHandler = (e) => {
    const noteLayers = map.getStyle().layers.filter(l => l.id.startsWith('notes-layer-')).map(l => l.id);
    if (!noteLayers.length) return;

    const features = map.queryRenderedFeatures(e.point, { layers: noteLayers });
    if (!features.length) return;

    e.originalEvent.stopImmediatePropagation();
    const props = features[0].properties;
    const trackId = props.track_id;
    const note = notesByTrackId[trackId]?.find(n => n.id === props.note_id);
    showNoteModal(props.point_id, note || null);
  };

  map.on('click', photoIconClickHandler);
  map.on('touchend', photoIconClickHandler);

  map.on('click', noteIconClickHandler);
  map.on('touchend', noteIconClickHandler);

  const pointClickHandler = async (e) => {
    const pointLayers = map.getStyle().layers.filter(l => l.id.startsWith('points-layer-')).map(l => l.id);
    if (!pointLayers.length) return;

    const features = map.queryRenderedFeatures(e.point, { layers: pointLayers });
    if (!features.length) return;

    const feature = features[0];
    const { properties, geometry } = feature;
    let { trackId, id, time: pointTime, speed, time_zone } = properties;
    const coordinates = geometry.coordinates.slice();

    // If timezone is missing or UTC/GMT, try to derive it from coordinates
    if (!time_zone || ['utc', 'gmt'].includes(time_zone.toLowerCase())) {
        time_zone = await getTimezoneFromLatLon(coordinates[1], coordinates[0]);
    }

    const time = DateTime.fromISO(pointTime).setZone(time_zone);

    const note = notesByTrackId[trackId]?.find(n => n.point_id === id);
    const noteButtonText = note ? "Edit Note" : "Add Note";

    const popupContent = `
        <div class="popup-content">
            <h4>Point Information</h4>
            <p>Time: ${time.toLocaleString(DateTime.TIME_SIMPLE)}</p>
            <p>Speed: ${speed} kts</p>
            <div class="popup-actions">
                <button class="popup-btn add-note-btn" data-point-id="${id}" title="${noteButtonText}"><span class="material-symbols-outlined">note_add</span></button>
                <button class="popup-btn trim-track-btn" data-track-id="${trackId}" data-point-time="${pointTime}" title="Trim Track"><span class="material-symbols-outlined">content_cut</span></button>
                <button class="popup-btn delete-point-btn" data-track-id="${trackId}" data-point-id="${id}" title="Delete Point"><span class="material-symbols-outlined">delete_forever</span></button>
            </div>
        </div>
    `;

    const popup = new mapboxgl.Popup({ offset: 25, closeButton: true })
      .setLngLat(coordinates)
      .setHTML(popupContent)
      .addTo(mapInstance);

    const popupEl = popup.getElement();
    const closeBtn = popupEl.querySelector('.popup-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => popup.remove());
    }
    const addNoteBtn = popupEl.querySelector('.add-note-btn');
    if (addNoteBtn) {
      addNoteBtn.addEventListener('click', () => showNoteModal(id, note));
    }
    const trimTrackBtn = popupEl.querySelector('.trim-track-btn');
    if (trimTrackBtn) {
      trimTrackBtn.addEventListener('click', handleTrimClick);
    }
    const deletePointBtn = popupEl.querySelector('.delete-point-btn');
    if (deletePointBtn) {
      deletePointBtn.addEventListener('click', handleDeletePointClick);
    }
  };

  map.on('click', pointClickHandler);
  map.on('touchend', pointClickHandler);
}

export async function drawTrackOnMap(track) {
    if (!track.color) {
      if (visibleTrackIds.size === 1) {
        track.color = defaultTrackColors[0];
        incrementColorIndex();
      } else {
        track.color = defaultTrackColors[colorIndex % defaultTrackColors.length];
        incrementColorIndex();
      }
    }

    // If timezone is missing or UTC/GMT, try to derive it from coordinates
    if ((!track.weather_report || !track.weather_report.time_zone || ['utc', 'gmt'].includes(track.weather_report.time_zone.toLowerCase())) && track.points && track.points.length > 0) {
        const tz = await getTimezoneFromLatLon(track.points[0].lat, track.points[0].lon);
        if (!track.weather_report) track.weather_report = {};
        track.weather_report.time_zone = tz;
    }
  
    const sourceId = `line-source-${track.id}`,
      layerId = `line-layer-${track.id}`;
    const pSourceId = `points-source-${track.id}`,
      pLayerId = `points-layer-${track.id}`;
    const addData = () => {
      if (!mapInstance.getSource(sourceId)) {
        mapInstance.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: track.points.map(p => [p.lon, p.lat])
            }
          }
        });
        mapInstance.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': track.color,
            'line-width': 3,
            'line-opacity': 0.8
          }
        }, 'photo-anchor');
      }
      if (!mapInstance.getSource(pSourceId)) {
        mapInstance.addSource(pSourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: track.points.map(p => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [p.lon, p.lat]
              },
              properties: {
                time: p.time,
                id: p.id,
                speed: p.speed.toFixed(1),
                color: track.color,
                trackId: track.id,
                time_zone: track.weather_report.time_zone
              }
            }))
          }
        });
        mapInstance.addLayer({
          id: pLayerId,
          type: 'circle',
          source: pSourceId,
          paint: {
            'circle-radius': 5,
            'circle-opacity': 0
          }
        });
      }
      [{
        p: track.points[0],
        t: 'Start'
      }, {        p: track.points[track.points.length - 1],
        t: 'Finish'
      }].forEach((m, i) => {
        const el = document.createElement('div');
        el.className = `marker custom-map-marker marker-track-${track.id} ${i === 0 ? 'start' : 'finish'}`;
        el.innerHTML = `<span><b>${m.t}</b></span>`;
        el.style.visibility = 'hidden';
        new mapboxgl.Marker(el).setLngLat([m.p.lon, m.p.lat]).addTo(mapInstance);
      });
    };
  
    addData();
    getNotesForTrack(track.id).then(notes => {
      if (notes) {
        notesByTrackId[track.id] = notes;
      }
    });
  }
  
  export function removeTrackFromMap(trackId) {
    if (!mapInstance) return;
  
    delete notesByTrackId[trackId];
    removePhotoIcons(trackId);
    removeNoteIcons(trackId);

    const layerIds = [`line-layer-${trackId}`, `points-layer-${trackId}`];
    const sourceIds = [`line-source-${trackId}`, `points-source-${trackId}`];
  
    layerIds.forEach(id => {
      if (mapInstance.getLayer(id)) {
        mapInstance.removeLayer(id);
      }
    });
  
    sourceIds.forEach(id => {
      if (mapInstance.getSource(id)) {
        mapInstance.removeSource(id);
      }
    });
  
    document.querySelectorAll(`.marker-track-${trackId}`).forEach(m => m.remove());
  }
  
  export function clearAllMapLayers() {
    if (!mapInstance || !mapInstance.isStyleLoaded()) return;
    const style = mapInstance.getStyle();
    const layers = style.layers.filter(l => 
        l.id.startsWith('line-layer-') || 
        l.id.startsWith('points-layer-') || 
        l.id.startsWith('notes-layer-') ||
        l.id.startsWith('notes-circle-layer-') ||
        l.id.startsWith('photos-layer-') ||
        l.id.startsWith('photos-circle-layer-')
    );
    const sources = new Set(layers.map(l => l.source));
    layers.forEach(l => {
      if (mapInstance.getLayer(l.id)) mapInstance.removeLayer(l.id)
    });
    sources.forEach(s => {
      if (mapInstance.getSource(s)) mapInstance.removeSource(s)
    });
    document.querySelectorAll(".custom-map-marker").forEach(m => m.remove());
  }
  
  function getLegendPadding() {
    const MOBILE_BREAKPOINT = 768;
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
        return 80; // Default padding on mobile
    }

    const legendEl = document.querySelector('header.legend');
    if (!legendEl || legendEl.classList.contains('hidden')) {
         return 80; // Default padding if legend is hidden
    }

    const style = window.getComputedStyle(legendEl);
    const margin = 20; // Add a buffer margin
    const legendWidth = legendEl.offsetWidth + (parseInt(style.marginLeft) || 0) + (parseInt(style.marginRight) || 0);
    const legendHeight = legendEl.offsetHeight + (parseInt(style.marginTop) || 0) + (parseInt(style.marginBottom) || 0);
    const rightPos = parseInt(style.right) || 0;
    const bottomPos = parseInt(style.bottom) || 0;

    return {
        top: 80,
        bottom: Math.max(80, legendHeight + bottomPos + margin),
        left: 80,
        right: Math.max(80, legendWidth + rightPos + margin)
    };
}

export function updateMapBounds() {
    if (visibleTrackIds.size === 0) {
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    visibleTrackIds.forEach(id => {
      const stats = getStats(trackDataCache[id]);
      if (stats) bounds.extend([stats.west, stats.south]).extend([stats.E, stats.north]);
    });
    if (!bounds.isEmpty()) {
        const padding = getLegendPadding();
        mapInstance.fitBounds(bounds, {
          padding: padding,
          duration: 500,
          maxZoom: 16
        });
    }
  }
  
  export function updateMarkersVisibility() {
    document.querySelectorAll('.custom-map-marker').forEach(m => m.style.visibility = 'hidden');
    if (visibleTrackIds.size === 1) {
      const [id] = visibleTrackIds;
      document.querySelectorAll(`.marker-track-${id}`).forEach(m => m.style.visibility = 'visible');
    }
  }
  
  function getStats(track) {
    if (!track.points.length) return null;
    let minLat = Infinity,
      maxLat = -Infinity,
      minLon = Infinity,
      maxLon = -Infinity;
    track.points.forEach(p => {
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lat < minLat) minLat = p.lat;
      if (p.lon > maxLon) maxLon = p.lon;
      if (p.lon < minLon) minLon = p.lon;
    });
    const crossesAntimeridian = (maxLon - minLon) > 180;
    return {
      north: maxLat,
      south: minLat,
      E: crossesAntimeridian ? minLon : maxLon,
      west: crossesAntimeridian ? maxLon : minLon
    };
  }
  
  export function makeDraggable(elmnt) {
    let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

    elmnt.onmousedown = dragMouseDown;

    function dragMouseDown(e) {
        e = e || window.event;

        const handle = e.target.closest('.title-container');
        if (!handle) return;
        
        if (e.target.getAttribute('contenteditable') === 'true' || e.target.closest('button, a, input, select')) {
            return;
        }

        e.preventDefault();

        // Set position explicitly and remove bottom constraint
        const rect = elmnt.getBoundingClientRect();
        elmnt.style.top = rect.top + 'px';
        elmnt.style.left = rect.left + 'px';
        elmnt.style.bottom = 'auto';
        elmnt.style.right = 'auto';

        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
    }

    function elementDrag(e) {
        e = e || window.event;
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
        elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
    }

    function closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
    }
}

export function drawPhotoIcons(trackId, photos) {
    const sourceId = `photos-source-${trackId}`;
    const layerId = `photos-layer-${trackId}`;
    const circleLayerId = `photos-circle-layer-${trackId}`;

    if (mapInstance.getSource(sourceId)) {
        removePhotoIcons(trackId);
    }

    if (!photos || photos.length === 0) {
        removePhotoIcons(trackId);
        return;
    }

    const photosByPoint = photos.reduce((acc, photo) => {
        const key = `${photo.point_lon},${photo.point_lat}`;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(photo);
        return acc;
    }, {});

    const features = [];
    Object.values(photosByPoint).forEach(photoGroup => {
        photoGroup.forEach((photo, index) => {
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [photo.point_lon, photo.point_lat]
                },
                properties: {
                    photo_id: photo.id,
                    signed_url: photo.signed_url,
                    caption: photo.caption,
                    can_delete: photo.can_delete,
                    author_name: photo.author_name,
                    offset: [0, index * -20]
                }
            });
        });
    });

    mapInstance.addSource(sourceId, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: features
        }
    });

    mapInstance.addLayer({
        id: circleLayerId,
        type: 'circle',
        source: sourceId,
        paint: {
            'circle-radius': 15,
            'circle-opacity': 1
        }
    });

    mapInstance.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
            'icon-image': 'attraction-15',
            'icon-size': 1.0,
            'icon-allow-overlap': true,
            'icon-offset': ['get', 'offset']
        }
    });

    if (!photosVisible) {
        const vis = 'none';
        mapInstance.setLayoutProperty(layerId, 'visibility', vis);
        mapInstance.setLayoutProperty(circleLayerId, 'visibility', vis);
    }
}

export function removePhotoIcons(trackId) {
    const sourceId = `photos-source-${trackId}`;
    const layerId = `photos-layer-${trackId}`;
    const circleLayerId = `photos-circle-layer-${trackId}`;

    if (mapInstance.getLayer(layerId)) {
        mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getLayer(circleLayerId)) {
        mapInstance.removeLayer(circleLayerId);
    }
    if (mapInstance.getSource(sourceId)) {
        mapInstance.removeSource(sourceId);
    }
}

export function drawNoteIcons(trackId, notes) {
    const sourceId = `notes-source-${trackId}`;
    const layerId = `notes-layer-${trackId}`;
    const circleLayerId = `notes-circle-layer-${trackId}`;

    if (mapInstance.getSource(sourceId)) {
        removeNoteIcons(trackId);
    }

    if (!notes || notes.length === 0) {
        removeNoteIcons(trackId);
        return;
    }

    const points = trackDataCache[trackId].points;
    mapInstance.addSource(sourceId, {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: notes.map(n => {
                const point = points.find(p => p.id === n.point_id);
                if (!point) return null;
                
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [point.lon, point.lat] 
                    },
                    properties: {
                        note_id: n.id,
                        point_id: n.point_id,
                        content: n.content,
                        track_id: trackId
                    }
                };
            }).filter(Boolean)
        }
    });

    mapInstance.addLayer({
        id: circleLayerId,
        type: 'circle',
        source: sourceId,
        paint: {
            'circle-radius': 15,
            'circle-opacity': 1
        }
    });

    mapInstance.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
            'icon-image': 'library-15',
            'icon-size': 1.0,
            'icon-allow-overlap': true
        }
    });

    if (!notesVisible) {
        const vis = 'none';
        mapInstance.setLayoutProperty(layerId, 'visibility', vis);
        mapInstance.setLayoutProperty(circleLayerId, 'visibility', vis);
    }
}

export function removeNoteIcons(trackId) {
    const sourceId = `notes-source-${trackId}`;
    const layerId = `notes-layer-${trackId}`;
    const circleLayerId = `notes-circle-layer-${trackId}`;

    if (mapInstance.getLayer(layerId)) {
        mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getLayer(circleLayerId)) {
        mapInstance.removeLayer(circleLayerId);
    }
    if (mapInstance.getSource(sourceId)) {
        mapInstance.removeSource(sourceId);
    }
}

export function setAllPhotoLayersVisibility(visible) {
    const vis = visible ? 'visible' : 'none';
    visibleTrackIds.forEach(trackId => {
        const layerId = `photos-layer-${trackId}`;
        const circleLayerId = `photos-circle-layer-${trackId}`;
        if (mapInstance.getLayer(layerId)) mapInstance.setLayoutProperty(layerId, 'visibility', vis);
        if (mapInstance.getLayer(circleLayerId)) mapInstance.setLayoutProperty(circleLayerId, 'visibility', vis);
    });
}

export function setAllNoteLayersVisibility(visible) {
    const vis = visible ? 'visible' : 'none';
    visibleTrackIds.forEach(trackId => {
        const layerId = `notes-layer-${trackId}`;
        const circleLayerId = `notes-circle-layer-${trackId}`;
        if (mapInstance.getLayer(layerId)) mapInstance.setLayoutProperty(layerId, 'visibility', vis);
        if (mapInstance.getLayer(circleLayerId)) mapInstance.setLayoutProperty(circleLayerId, 'visibility', vis);
    });
}